/* eslint-disable react/prop-types */
import React, { useMemo } from "react";
import type { RenderBarLoaderNode as RenderBarLoaderNodeModel } from "../../../util/src/layout-compiler/types.js";
import { getTextDecorationCssProps } from "../display-editor/textDecorationCss.js";
import {
  type BarLoaderBlockStyle,
  type BarLoaderDiskDefinition,
  type BarLoaderProps,
  type BarLoaderTargetPatch,
  buildBarLoaderDiskStylesForRender,
  calculateLoadedDisks,
  getBarLoaderFootprint,
  getBarLoaderWidthDivisor,
  getEnabledDisks,
  isBarLoaderCompactVariableWidth,
} from "../display-editor/components/barLoaderConfig.js";
import { nodeStyleWithoutDimensions } from "./nodeStyleHelpers.js";
import { getRenderNodeClipStyle } from "./renderClipStyle.js";
import { useRuntimeNodeProps } from "./useRuntimeElementRef.js";

type RenderBarLoaderNodeProps = {
  node: RenderBarLoaderNodeModel;
  scale: number;
};

function textColor(bg: string): string {
  return ["#ff3b30", "#007aff", "#34c759", "#1c1c1e", "#8b0000"].includes(
    bg.toLowerCase(),
  )
    ? "white"
    : "black";
}

function scalePxInString(value: string, scale: number): string {
  return value.replace(
    /(-?\d*\.?\d+)px\b/g,
    (_, rawNumber: string) => `${Number(rawNumber) * scale}px`,
  );
}

function toCssLength(value: unknown, scale: number): string | undefined {
  if (typeof value === "number" && Number.isFinite(value)) {
    return `${Math.max(0, value) * scale}px`;
  }
  if (typeof value === "string" && value.trim().length > 0) {
    return scalePxInString(value.trim(), scale);
  }
  return undefined;
}

function toLengthPx(value: unknown, fallback: number): number {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string" && value.trim().length > 0) {
    const parsed = Number.parseFloat(value.replace("px", "").trim());
    if (Number.isFinite(parsed)) return parsed;
  }
  return fallback;
}

function toBlockStyle(
  style: BarLoaderBlockStyle,
  renderScale: number,
): React.CSSProperties {
  // Keep only longhands: React can otherwise remove a corner while updating
  // the shorthand, and reports a conflict on every live weight change.
  const [horizontal = "", vertical] = String(style.borderRadius ?? "").split(
    "/",
  );
  const expand = (value: string) => {
    const parts = value
      .trim()
      .split(/\s+(?![^()]*\))/)
      .filter(Boolean);
    return [
      parts[0],
      parts[1] ?? parts[0],
      parts[2] ?? parts[0],
      parts[3] ?? parts[1] ?? parts[0],
    ];
  };
  const horizontalCorners = expand(horizontal);
  const verticalCorners =
    vertical == null ? horizontalCorners : expand(vertical);
  const radius = (index: number, override: unknown) =>
    toCssLength(
      override ??
        (horizontalCorners[index] == null
          ? undefined
          : `${horizontalCorners[index]} ${verticalCorners[index]}`),
      renderScale,
    );
  return {
    display: style.visibility === false ? "none" : undefined,
    width: toCssLength(style.width, renderScale),
    height: toCssLength(style.height, renderScale),
    margin: toCssLength(style.margin, renderScale),
    marginTop: toCssLength(style.marginTop, renderScale),
    marginRight: toCssLength(style.marginRight, renderScale),
    marginBottom: toCssLength(style.marginBottom, renderScale),
    marginLeft: toCssLength(style.marginLeft, renderScale),
    padding: toCssLength(style.padding, renderScale),
    paddingTop: toCssLength(style.paddingTop, renderScale),
    paddingRight: toCssLength(style.paddingRight, renderScale),
    paddingBottom: toCssLength(style.paddingBottom, renderScale),
    paddingLeft: toCssLength(style.paddingLeft, renderScale),
    backgroundColor: style.backgroundColor,
    opacity: style.opacity,
    transform: style.transform,
    border:
      typeof style.border === "string"
        ? scalePxInString(style.border, renderScale)
        : (style.border as React.CSSProperties["border"]),
    borderTop:
      typeof style.borderTop === "string"
        ? scalePxInString(style.borderTop, renderScale)
        : style.borderTop,
    borderRight:
      typeof style.borderRight === "string"
        ? scalePxInString(style.borderRight, renderScale)
        : style.borderRight,
    borderBottom:
      typeof style.borderBottom === "string"
        ? scalePxInString(style.borderBottom, renderScale)
        : style.borderBottom,
    borderLeft:
      typeof style.borderLeft === "string"
        ? scalePxInString(style.borderLeft, renderScale)
        : style.borderLeft,
    borderTopLeftRadius: radius(0, style.borderTopLeftRadius),
    borderTopRightRadius: radius(1, style.borderTopRightRadius),
    borderBottomRightRadius: radius(2, style.borderBottomRightRadius),
    borderBottomLeftRadius: radius(3, style.borderBottomLeftRadius),
    color: style.color,
    fontSize: toCssLength(style.fontSize, renderScale),
    fontFamily: style.fontFamily,
    fontWeight: style.fontWeight,
    textAlign: style.textAlign,
    textTransform: style.textTransform,
    whiteSpace: style.whiteSpace,
    ...getTextDecorationCssProps(style as Record<string, unknown>, renderScale),
    boxSizing: "border-box",
  };
}

function composeTransform(
  rotation: number,
  styleTransform?: unknown,
): string | undefined {
  const rotationTransform =
    rotation !== 0 ? `rotate(${rotation}deg)` : undefined;
  const extra =
    typeof styleTransform === "string" && styleTransform.trim().length > 0
      ? styleTransform
      : undefined;

  if (rotationTransform && extra) {
    return `${rotationTransform} ${extra}`;
  }

  return rotationTransform ?? extra;
}

export function RenderBarLoaderNode({ node, scale }: RenderBarLoaderNodeProps) {
  const runtimeProps = useRuntimeNodeProps(node);
  const props = node.props as BarLoaderProps;
  const clipStyle = getRenderNodeClipStyle(node, scale);
  const mode = props.mode ?? "full";
  const unit = props.unit ?? "kg";
  const labelOrientation = props.labelOrientation ?? "horizontal";
  const totalWeight = Number(props.totalWeight ?? 0);

  const targetPatches = node.barLoaderTargetPatches as
    | BarLoaderTargetPatch[]
    | undefined;

  const diskStyles = useMemo(
    () => buildBarLoaderDiskStylesForRender(props, targetPatches),
    [props, targetPatches],
  );

  const diskDefinitions = getEnabledDisks(unit, props.optionalDisks);
  const loadedDisks = calculateLoadedDisks(
    totalWeight,
    unit,
    props.optionalDisks,
    props.barWeight,
    props.collarsWeight,
  );
  const loadedDiskItems = loadedDisks
    .map((weight) => diskDefinitions.find((disk) => disk.weight === weight))
    .filter((disk): disk is BarLoaderDiskDefinition => Boolean(disk));

  const nodeWidth = Number.isFinite(node.width) ? node.width : 0;
  const nodeHeight = Number.isFinite(node.height) ? node.height : 0;

  const getLabelHeight = (disk: BarLoaderDiskDefinition): number =>
    toLengthPx(diskStyles[String(disk.weight)]?.label?.height, 56);
  const getDiskHeight = (disk: BarLoaderDiskDefinition): number =>
    toLengthPx(diskStyles[String(disk.weight)]?.disk?.height, 112);
  const getLabelWidth = (disk: BarLoaderDiskDefinition): number =>
    toLengthPx(diskStyles[String(disk.weight)]?.label?.width, 60);
  const getDiskWidth = (disk: BarLoaderDiskDefinition): number =>
    toLengthPx(diskStyles[String(disk.weight)]?.disk?.width, 60);

  const footprintDims = getBarLoaderFootprint(
    { ...props, totalWeight },
    diskStyles,
  );
  const fullModeHeightDelta = nodeHeight - footprintDims.height;
  const compactModeHeightDelta = nodeHeight - footprintDims.height;
  const labelHeightIncrement = mode === "full" ? fullModeHeightDelta / 2 : 0;
  const diskHeightIncrement =
    mode === "full" ? fullModeHeightDelta / 2 : compactModeHeightDelta;

  const widthDivisor = getBarLoaderWidthDivisor(props);
  const baseTotalWidth = footprintDims.width;
  const widthDelta = isBarLoaderCompactVariableWidth(props)
    ? 0
    : nodeWidth - baseTotalWidth;
  const widthIncrementPerItem = widthDelta / widthDivisor;

  const adjustLabelStyle = (
    disk: BarLoaderDiskDefinition,
  ): BarLoaderBlockStyle => {
    const styleEntry = diskStyles[String(disk.weight)];
    const label = styleEntry?.label ?? {};
    return {
      ...label,
      width: Math.max(0, getLabelWidth(disk) + widthIncrementPerItem),
      height: Math.max(0, getLabelHeight(disk) + labelHeightIncrement),
    };
  };

  const adjustDiskStyle = (
    disk: BarLoaderDiskDefinition,
  ): BarLoaderBlockStyle => {
    const styleEntry = diskStyles[String(disk.weight)];
    const diskStyle = styleEntry?.disk ?? {};
    return {
      ...diskStyle,
      width: Math.max(0, getDiskWidth(disk) + widthIncrementPerItem),
      height: Math.max(0, getDiskHeight(disk) + diskHeightIncrement),
    };
  };

  const rootStyle = nodeStyleWithoutDimensions(
    node.style,
  ) as unknown as BarLoaderBlockStyle;
  const rootBlockStyle = toBlockStyle(rootStyle, scale);
  const composedTransform = composeTransform(
    node.rotation,
    rootBlockStyle.transform,
  );

  const rootWithRotationStyle: React.CSSProperties = {
    ...rootBlockStyle,
    transform: composedTransform,
    transformOrigin: "center center",
  };

  const getDiskCount = (weight: number) =>
    loadedDisks.filter((w) => w === weight).length;

  const compactJustifyContent =
    props.compactDiskAlign === "start"
      ? "flex-start"
      : props.compactDiskAlign === "end"
        ? "flex-end"
        : "center";

  const renderLabel = (disk: BarLoaderDiskDefinition) => {
    const styleEntry = diskStyles[String(disk.weight)];
    const labelStyle = adjustLabelStyle(disk);
    const labelText = styleEntry?.labelText ?? String(disk.weight);
    return (
      <div
        style={{
          ...toBlockStyle(labelStyle, scale),
          display: labelStyle.visibility === false ? "none" : "flex",
          alignItems: "center",
          justifyContent: "center",
          color: labelStyle.color ?? "#fff",
          writingMode:
            labelOrientation === "vertical" ? "vertical-rl" : undefined,
          textOrientation:
            labelOrientation === "vertical" ? "upright" : undefined,
        }}
      >
        {labelText}
      </div>
    );
  };

  const renderDiskBlock = (
    disk: BarLoaderDiskDefinition,
    content: React.ReactNode,
  ) => {
    const diskStyle = adjustDiskStyle(disk);
    const bg = diskStyle.backgroundColor ?? disk.defaultColor;
    return (
      <div
        style={{
          ...toBlockStyle(diskStyle, scale),
          display: diskStyle.visibility === false ? "none" : "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: bg,
          color: diskStyle.color ?? textColor(bg),
          border:
            diskStyle.border ??
            (bg.toLowerCase() === "#f5f5f5" ? "1px solid #000" : undefined),
          writingMode:
            labelOrientation === "vertical" ? "vertical-rl" : undefined,
          textOrientation:
            labelOrientation === "vertical" ? "upright" : undefined,
        }}
      >
        {content}
      </div>
    );
  };

  const compactModesOuterStyle: React.CSSProperties = {
    ...rootWithRotationStyle,
    width: "100%",
    height: "100%",
    display: "flex",
    flexDirection: "row",
    justifyContent: compactJustifyContent,
    alignItems: "center",
    overflow: "hidden",
  };

  const content =
    mode === "small" ? (
      <div style={compactModesOuterStyle}>
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            flexShrink: 0,
          }}
        >
          {diskDefinitions
            .filter((disk) => getDiskCount(disk.weight) > 0)
            .map((disk, index) => (
              <React.Fragment key={`${disk.weight}-${index}`}>
                {renderDiskBlock(disk, getDiskCount(disk.weight))}
              </React.Fragment>
            ))}
        </div>
      </div>
    ) : mode === "bar" ? (
      <div style={compactModesOuterStyle}>
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "stretch",
            flexShrink: 0,
          }}
        >
          {loadedDiskItems.map((disk, index) => {
            const styleEntry = diskStyles[String(disk.weight)];
            return (
              <div
                key={`${disk.weight}-${index}`}
                style={{ display: "flex", flexDirection: "column" }}
              >
                {renderDiskBlock(
                  disk,
                  styleEntry?.labelText ?? String(disk.weight),
                )}
              </div>
            );
          })}
        </div>
      </div>
    ) : (
      <div
        style={{
          ...rootWithRotationStyle,
          width: "100%",
          height: "100%",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            boxSizing: "border-box",
            width: "100%",
            display: "flex",
            flexDirection: "row",
            justifyContent: "flex-start",
          }}
        >
          {diskDefinitions.map((disk, index) => {
            const count = getDiskCount(disk.weight);
            return (
              <div
                key={index}
                style={{
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                {renderLabel(disk)}
                {count > 0 && renderDiskBlock(disk, count)}
              </div>
            );
          })}
        </div>
      </div>
    );

  const fitContent =
    node.props.compactWidthMode === "variable" && node.props.mode !== "full";

  return (
    <div
      {...runtimeProps}
      style={{
        ...(node.style.position === "relative"
          ? {
              position: "relative",
              flexShrink: 0,
            }
          : {
              position: "absolute",
              left: node.x * scale,
              top: node.y * scale,
            }),
        width: fitContent ? "fit-content" : node.width * scale,
        height: node.height * scale,
        zIndex: node.zIndex,
        boxSizing: "border-box",
        overflow: "hidden",
        ...clipStyle,
      }}
    >
      {content}
    </div>
  );
}
